import { ChangeEventHandler, FC, FocusEventHandler, FormEventHandler, Ref, useRef, useState } from 'react';

import { TextField, TextFieldProps } from '@purpur/library';

import { useComponentId } from '@telialt/utils/src/hooks';

export type TextInputProps = Omit<TextFieldProps, 'valid' | 'id' | 'errorText' | 'data-testid'> & {
    dataTest: string;
    pattern?: string;
    requiredErrorMessage?: string;
    patternErrorMessage?: string;
    minLengthErrorMessage?: string;
    maxLengthErrorMessage?: string;
    showValidIcon?: boolean;
    errorMessage?: string;
    id?: string;
    inputRef?: Ref<HTMLInputElement>;
};

export const TextInput: FC<TextInputProps> = ({
    defaultValue,
    required,
    requiredErrorMessage = '',
    pattern,
    patternErrorMessage = '',
    minLength,
    minLengthErrorMessage = '',
    maxLength,
    maxLengthErrorMessage = '',
    dataTest,
    showValidIcon = false,
    onChange,
    onBlur,
    errorMessage,
    id,
    clearButtonAriaLabel,
    onClear,
    inputRef: itemRef,
    disabled,
    onInvalid,
    ...props
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const generatedId = `text-input-${useComponentId()}`;

    const [errorText, setErrorText] = useState<string>('');
    const [isPristine, setIsPristine] = useState<boolean>(true);
    const [isValid, setIsValid] = useState<boolean>(false);
    const [inputValue, setInputValue] = useState<string>(String(defaultValue || ''));

    const validateInput = (eventValue: string, isInputPristine: boolean = isPristine) => {
        let validationRegEx: RegExp | undefined;
        if (pattern) {
            validationRegEx = new RegExp(pattern);
        }

        if (!isInputPristine) {
            if (required && eventValue === '') {
                setErrorText(requiredErrorMessage);
                inputRef.current?.setCustomValidity(requiredErrorMessage);
                setIsValid(false);
            } else if (pattern && eventValue !== '' && !validationRegEx?.test(eventValue)) {
                setErrorText(patternErrorMessage);
                inputRef.current?.setCustomValidity(patternErrorMessage);
                setIsValid(false);
            } else if (minLength && eventValue.length < minLength) {
                setErrorText(minLengthErrorMessage);
                inputRef.current?.setCustomValidity(minLengthErrorMessage);
                setIsValid(false);
            } else if (maxLength && eventValue.length > maxLength) {
                setErrorText(maxLengthErrorMessage);
                inputRef.current?.setCustomValidity(maxLengthErrorMessage);
                setIsValid(false);
            } else {
                setErrorText('');
                inputRef.current?.setCustomValidity('');
                setIsValid(true);
            }
        }
    };

    const onTextInputBlur: FocusEventHandler<HTMLInputElement> = (event) => {
        setIsPristine(false);
        setInputValue(event.target.value);
        validateInput(event.target.value, false);
        onBlur?.(event);
    };

    const onTextInputChange: ChangeEventHandler<HTMLInputElement> = (event) => {
        setInputValue(event.target.value);
        validateInput(event.target.value);
        onChange?.(event);
    };

    const onInvalidCheck: FormEventHandler<HTMLInputElement> = (event) => {
        event.preventDefault();
        onInvalid?.(event);
        setIsPristine(false);
        validateInput(inputValue, false);
    };

    return (
        <TextField
            // Otherwise it seems to be too complex for TypeScript
            {...(props as any)}
            ref={itemRef || inputRef}
            id={id || generatedId}
            defaultValue={defaultValue}
            required={required}
            pattern={pattern}
            errorText={errorMessage || (!disabled ? errorText : '')}
            data-test={dataTest}
            onBlur={onTextInputBlur}
            onChange={onTextInputChange}
            onInvalid={onInvalidCheck}
            valid={showValidIcon && !errorMessage && inputValue !== '' && isValid}
            disabled={disabled}
            clearButtonAriaLabel={clearButtonAriaLabel}
            onClear={onClear}
        />
    );
};
